import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Button } from 'foundations-components/transitional/components/Button';
import { IconButton } from 'foundations-components/transitional/components/IconButton';
import { DateInput } from 'foundations-components/transitional/components/DateInput';
import { AddIcon, RemoveIcon } from 'foundations-assets';
import { TimeInput } from 'foundations-components/transitional/components/TimeInput';
import { MultiColumn, MultiColumnGroup, MultiColumnItem } from 'foundations-components/transitional/components/MultiColumn';
import { useEffect, useState } from 'react';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { Select } from 'foundations-components/transitional/components/Select';
// @ts-expect-error Untyped module
import PageExpiryExternalUrlModal from 'ContentEditorUI/components/settings/PageExpiryExternalUrlModal';
// @ts-expect-error Untyped module
import { fetch, search } from 'ContentEditorUI/api/PagesApi';
import { useSelector } from 'react-redux';
import { getIsLandingPage, getIsSitePage } from 'ContentEditorUI/redux/selectors/contentReadOnlyDataSelectors';
import useContentCrmObject from '../../hooks/useContentCrmObject';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const UnpublishRedirectUrlField = ({
  disabled,
  pageExpiryRedirectUrl,
  pageExpiryRedirectId,
  setPageExpiryRedirectUrl,
  setPageExpiryRedirectId,
  unpublishExpiryUrl,
  setUnpublishExpiryUrl,
  loadRedirectUrlOptions
}) => {
  const {
    canScheduleWithContentCrmObject
  } = useContentCrmObject();
  const [redirectUrlSearchQuery, setRedirectUrlSearchQuery] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [pageExpiryRedirectName, setPageExpiryRedirectName] = useState(null);
  useEffect(() => {
    if (pageExpiryRedirectId && !pageExpiryRedirectName && !canScheduleWithContentCrmObject) {
      fetch(pageExpiryRedirectId).then(page => setPageExpiryRedirectName(page.name)).catch(() => {
        // no op
      });
    }
  });
  const [pagesMap, setPagesMap] = useState({});
  const redirectUrlLoadOptions = (input, callback) => {
    setRedirectUrlSearchQuery(input);
    if (canScheduleWithContentCrmObject && loadRedirectUrlOptions) {
      loadRedirectUrlOptions(input, callback);
      return;
    }
    const urlToPageMap = {};
    // TODO: as part of reducing content-type specific logic in CEUI, I'll try to clean this up and ask for options loader from the consumer app
    // see comment: https://git.hubteam.com/HubSpot/ContentEditorUI/pull/19030#pullrequestreview-4240159
    search(input).then(resp => {
      callback(null, {
        options: resp.objects.map(page => {
          urlToPageMap[page.url] = page;
          return {
            text: page.name ? `${page.name} (${page.url})` : page.url,
            value: page.url
          };
        }).filter(page => {
          return unpublishExpiryUrl ? page.value !== unpublishExpiryUrl : page.value !== pageExpiryRedirectUrl;
        })
      });
      setPagesMap(Object.assign({}, pagesMap, urlToPageMap));
    }).catch(() => {
      // no op
    });
  };
  const redirectUrlOptions = () => {
    // These options are used for the `options` attribute of Select,
    // and are used to provide available options for the selected value
    // before the user types to search. Without these options, the Select
    // could only show the value (which would be the selected expiry URL).
    const options = [];
    if (unpublishExpiryUrl) {
      options.push({
        text: unpublishExpiryUrl,
        value: unpublishExpiryUrl
      });
    } else {
      if (pageExpiryRedirectId && pageExpiryRedirectName && pageExpiryRedirectUrl) {
        options.push({
          text: pageExpiryRedirectName ? `${pageExpiryRedirectName} (${pageExpiryRedirectUrl})` : pageExpiryRedirectUrl,
          value: pageExpiryRedirectUrl
        });
      } else if (pageExpiryRedirectId === 0 && pageExpiryRedirectUrl) {
        // If the pageExpiryRedirectId is 0, then it's an external URL.
        options.push({
          text: pageExpiryRedirectUrl,
          value: pageExpiryRedirectUrl
        });
      }
    }
    return options;
  };
  const handleRedirectChange = ({
    target: {
      value
    }
  }) => {
    if (canScheduleWithContentCrmObject) {
      setUnpublishExpiryUrl(value);
    } else {
      setPageExpiryRedirectUrl(value);
      setPageExpiryRedirectId(value === '' ? undefined : pagesMap[value].id);
    }
  };
  const handleCreateExternalUrl = (externalUrlPrefix, externalUrlPath) => {
    if (canScheduleWithContentCrmObject) {
      setUnpublishExpiryUrl(`${externalUrlPrefix}${externalUrlPath}`);
    } else {
      setPageExpiryRedirectId(0);
      setPageExpiryRedirectUrl(`${externalUrlPrefix}${externalUrlPath}`);
    }
    setModalOpen(false);
  };
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(FormControl, {
      label: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "patagoniaPublishingOptionsModal.unpublishRedirect.header"
      }),
      marginBlockStart: "400",
      marginBlockEnd: "600",
      children: /*#__PURE__*/_jsx(Select, {
        "data-test-id": "page-expiration-url",
        autoload: true,
        clearable: true,
        defaultValue: unpublishExpiryUrl || pageExpiryRedirectUrl,
        dropdownFooter: /*#__PURE__*/_jsxs(Button, {
          onClick: () => setModalOpen(true),
          use: "link",
          children: [/*#__PURE__*/_jsx(AddIcon, {}), /*#__PURE__*/_jsx(FormattedMessage, {
            message: "patagoniaPublishingOptionsModal.unpublishRedirect.externalUrlText"
          })]
        }),
        loadOptions: redirectUrlLoadOptions,
        options: redirectUrlOptions(),
        noResultsText: I18n.text(redirectUrlSearchQuery.length === 0 ? 'patagoniaPublishingOptionsModal.unpublishRedirect.redirectUrlTypeToSearch' : 'patagoniaPublishingOptionsModal.unpublishRedirect.redirectUrlNoResultsFound'),
        onChange: handleRedirectChange,
        placeholder: I18n.text('patagoniaPublishingOptionsModal.unpublishRedirect.redirectUrlPlaceholder'),
        value: unpublishExpiryUrl || pageExpiryRedirectUrl,
        readOnly: disabled
      })
    }), modalOpen ? /*#__PURE__*/_jsx(PageExpiryExternalUrlModal // TODO: Rename this modal to be more generic
    , {
      closeModal: () => setModalOpen(false),
      onCreateExternalUrl: handleCreateExternalUrl
    }) : null]
  });
};
const SchedulingUnpublishControls = ({
  scheduledUnpublishDate,
  setScheduledUnpublishDate,
  scheduledUnpublishTime,
  setScheduledUnpublishTime,
  isReadOnlyMode,
  onExitSchedulingClick,
  minimumDateForUnpublish,
  minimumTimeForUnpublish,
  pageExpiryRedirectUrl,
  pageExpiryRedirectId,
  setPageExpiryRedirectUrl,
  setPageExpiryRedirectId,
  unpublishExpiryUrl,
  setUnpublishExpiryUrl,
  unpublishRedirectOptions,
  maxUnpublishDate
}) => {
  // TODO: remove these content type checks after CMP ungate
  const isLandingPage = useSelector(getIsLandingPage);
  const isSitePage = useSelector(getIsSitePage);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(MultiColumn, {
      flush: true,
      children: /*#__PURE__*/_jsxs(MultiColumnGroup, {
        children: [/*#__PURE__*/_jsx(MultiColumnItem, {
          horizontalAlign: "left",
          verticalAlign: "middle",
          paddingInline: "200",
          children: /*#__PURE__*/_jsx(DateInput, {
            "data-test-id": "unpublishing-date-picker",
            min: minimumDateForUnpublish,
            max: maxUnpublishDate,
            onChange: ({
              target: {
                value
              }
            }) => {
              setScheduledUnpublishDate(value);
            },
            value: scheduledUnpublishDate,
            readOnly: isReadOnlyMode
          })
        }), /*#__PURE__*/_jsx(MultiColumnItem, {
          horizontalAlign: "left",
          verticalAlign: "middle",
          paddingInline: "200",
          children: /*#__PURE__*/_jsx(TimeInput, {
            value: scheduledUnpublishTime,
            "data-test-id": "unpublishing-time-picker",
            use: "default",
            onChange: e => {
              setScheduledUnpublishTime(e.target.value);
            },
            readOnly: isReadOnlyMode,
            format: `LT [${I18n.moment.portalTz().format('z')}]`,
            interval: 15,
            min: minimumTimeForUnpublish
          })
        }), /*#__PURE__*/_jsx(MultiColumnItem, {
          horizontalAlign: "right",
          verticalAlign: "middle",
          paddingInline: "200",
          children: /*#__PURE__*/_jsx(IconButton, {
            use: "unstyled",
            onClick: onExitSchedulingClick,
            children: /*#__PURE__*/_jsx(RemoveIcon, {})
          })
        })]
      })
    }), isLandingPage || isSitePage || unpublishRedirectOptions !== null && unpublishRedirectOptions !== void 0 && unpublishRedirectOptions.allowRedirectOnUnpublish ? /*#__PURE__*/_jsx(UnpublishRedirectUrlField, {
      disabled: isReadOnlyMode,
      pageExpiryRedirectUrl: pageExpiryRedirectUrl,
      pageExpiryRedirectId: pageExpiryRedirectId,
      setPageExpiryRedirectUrl: setPageExpiryRedirectUrl,
      setPageExpiryRedirectId: setPageExpiryRedirectId,
      unpublishExpiryUrl: unpublishExpiryUrl,
      setUnpublishExpiryUrl: setUnpublishExpiryUrl,
      loadRedirectUrlOptions: unpublishRedirectOptions === null || unpublishRedirectOptions === void 0 ? void 0 : unpublishRedirectOptions.loadRedirectUrlOptions
    }) : null]
  });
};
export default SchedulingUnpublishControls;